/**
 * Preferencias de apariencia: se guardan en el navegador (no en la API) y se validan al leerlas,
 * porque `localStorage` lo puede editar cualquiera.
 */
import { guardarPreferencia, leerPreferencia } from '../../lib/almacenamiento';
import { normalizarHex } from './color';
import { DEGRADADOS, PALETAS } from './paletas';

export type Modo = 'sistema' | 'claro' | 'oscuro';

export type Densidad = 'comoda' | 'compacta';
export type Esfuerzo = 'mostrar' | 'ocultar';

export interface PreferenciasTema {
  modo: Modo;
  /** `id` de una paleta predefinida; `null` = colores de siempre según el modo. */
  paleta: string | null;
  /** Fondo de color liso (`#rrggbb`); excluye al degradado. */
  fondoColor: string | null;
  /** `id` de un degradado predefinido; excluye al color liso. */
  degradado: string | null;
  /** Espacio dentro de las tarjetas y entre ellas del tablero. */
  densidad: Densidad;
  /** Si las tarjetas muestran la etiqueta de esfuerzo (llega en un PR posterior). */
  esfuerzo: Esfuerzo;
}

export const PREFERENCIAS_INICIALES: PreferenciasTema = {
  modo: 'sistema',
  paleta: null,
  fondoColor: null,
  degradado: null,
  densidad: 'comoda',
  esfuerzo: 'mostrar',
};

const CLAVE = 'tema';

/** Convierte cualquier cosa en preferencias válidas; lo que no calza vuelve al valor inicial. */
export function sanearPreferencias(crudo: unknown): PreferenciasTema {
  if (typeof crudo !== 'object' || crudo === null) return PREFERENCIAS_INICIALES;
  const o = crudo as Record<string, unknown>;
  const modo: Modo = o.modo === 'claro' || o.modo === 'oscuro' ? o.modo : 'sistema';
  const paleta = PALETAS.find((p) => p.id === o.paleta)?.id ?? null;
  const degradado = DEGRADADOS.find((d) => d.id === o.degradado)?.id ?? null;
  const fondoColor = degradado ? null : normalizarHex(o.fondoColor);
  const densidad: Densidad = o.densidad === 'compacta' ? 'compacta' : 'comoda';
  const esfuerzo: Esfuerzo = o.esfuerzo === 'ocultar' ? 'ocultar' : 'mostrar';
  return { modo, paleta, fondoColor, degradado, densidad, esfuerzo };
}

export function leerPreferenciasTema(): PreferenciasTema {
  const texto = leerPreferencia(CLAVE);
  if (!texto) return PREFERENCIAS_INICIALES;
  try {
    return sanearPreferencias(JSON.parse(texto));
  } catch {
    return PREFERENCIAS_INICIALES;
  }
}

export function guardarPreferenciasTema(preferencias: PreferenciasTema): void {
  guardarPreferencia(CLAVE, JSON.stringify(preferencias));
}
